import { useCallback } from 'react'
import * as Sentry from '@sentry/nextjs'

import { useAuthContext } from 'components/Auth/AuthContextProvider'

import serializeParameters, { Parameters } from './serializeParameters'
import useIWitnessAccessToken from './useIWitnessAccessToken'

const baseUrl = process.env.NEXT_PUBLIC_IWALK_API_BASE_URL ?? ''

export type { Parameters }

interface ApiFetcherProps {
  endpoint: string
  token: string
  params?: Parameters
}

interface ApiInteraction<T> {
  accessToken: () => string
  get: (props: ApiFetcherProps) => Promise<T>
  postWithUserToken: (props: ApiFetcherProps) => Promise<T>
}

function useApiInteraction<T>(enabled?: boolean): ApiInteraction<T> {
  const { user } = useAuthContext()
  const token = useIWitnessAccessToken(enabled)

  const accessToken = useCallback(() => {
    if (token == null) {
      throw new Error('Expected non-null access token')
    }

    return token.access_token
  }, [token])

  const postWithUserToken = useCallback(
    async ({ endpoint, token, params }: ApiFetcherProps) => {
      const url = `${baseUrl}${endpoint}`

      // n.b. vhap-sso seems to constantly new user tokens for us when
      // returning to the site, so we've pulled `UserToken` out from the "key"
      // passed to `useSWR` so that we do not making redunant POST requests
      // when switching tabs

      const paramsWithUserToken = {
        ...params,
        UserToken: user?.userToken ?? '',
      }

      return fetcher<T>(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paramsWithUserToken),
      })
    },
    [user?.userToken]
  )

  const get = useCallback(
    async ({ endpoint, token, params }: ApiFetcherProps) => {
      const query = serializeParameters(params)
      const url = `${baseUrl}${endpoint}${query ? `?${query}` : ''}`

      return fetcher<T>(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
    },
    []
  )

  return { accessToken, postWithUserToken, get }
}

async function fetcher<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init)

  if (!response.ok) {
    const error = new Error(
      `An error occurred while fetching data: ${response.status} for ${url}`
    )

    Sentry.captureException(error)

    throw error
  }

  try {
    return (await response.json()) as T
  } catch (err) {
    return {} as T
  }
}

export default useApiInteraction
